import { useState, useEffect } from 'react';
import { Hotel } from '@types';
import { hotelService } from '../services';

type SortOption = 'name' | 'rooms' | 'stars' | 'renovations';
type SortDirection = 'asc' | 'desc';

/**
 * Hook for fetching and managing hotel list with sorting and filtering
 */
export const useHotelList = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [filteredHotels, setFilteredHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        console.log('🏨 useHotelList: Fetching hotels...');
        const data = await hotelService.getHotels();
        console.log('🏨 useHotelList: Received hotels data:', {
          count: data.length,
          firstHotel: data[0] ? {
            id: data[0].id,
            name: data[0].name,
            estimated_value: data[0].estimated_value,
            last_valuation_date: data[0].last_valuation_date
          } : null
        });
        setHotels(data);
        setFilteredHotels(data);
      } catch (error) {
        console.error('❌ Error fetching hotels:', error);
        setError('Failed to load hotels');
      } finally {
        setLoading(false);
      }
    };
    fetchHotels();
  }, []);

  useEffect(() => {
    // Apply search filter and sorting
    let result = [...hotels];

    // Search filter
    if (searchTerm) {
      const lowercaseTerm = searchTerm.toLowerCase();
      result = result.filter(
        hotel =>
          hotel.name?.toLowerCase().includes(lowercaseTerm) ||
          hotel.city?.toLowerCase().includes(lowercaseTerm) ||
          hotel.state?.toLowerCase().includes(lowercaseTerm) ||
          hotel.property_type?.toLowerCase().includes(lowercaseTerm)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'rooms':
          comparison = (a.num_rooms || 0) - (b.num_rooms || 0);
          break;
        case 'stars':
          comparison = (a.star_rating || 0) - (b.star_rating || 0);
          break;
        case 'renovations':
          const aTotal = a.renovation_history?.reduce((sum, reno) => sum + (reno.cost || 0), 0) || 0;
          const bTotal = b.renovation_history?.reduce((sum, reno) => sum + (reno.cost || 0), 0) || 0;
          comparison = aTotal - bTotal;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredHotels(result);
  }, [hotels, searchTerm, sortBy, sortDirection]);

  const deleteHotel = async (hotelId: string): Promise<boolean> => {
    try {
      await hotelService.deleteHotel(hotelId);
      setHotels(hotels.filter(hotel => hotel.id !== hotelId));
      return true;
    } catch (error) {
      console.error('Error deleting hotel:', error);
      setError('Failed to delete hotel');
      return false;
    }
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return {
    hotels,
    filteredHotels,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    sortDirection,
    toggleSortDirection,
    successMessage,
    setSuccessMessage,
    deleteHotel,
    setError
  };
};